import { Component, OnInit, inject, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ApiService } from '../../core/services/api.service';
import { SupportTicket, SupportTicketStatus } from '../../core/models/api.models';

@Component({
  selector: 'app-support-ticket-detail',
  standalone: true,
  imports: [DatePipe, FormsModule, RouterLink],
  templateUrl: './support-ticket-detail.component.html',
  styleUrl: './support-ticket-detail.component.scss',
})
export class SupportTicketDetailComponent implements OnInit {
  private readonly api = inject(ApiService);
  private readonly route = inject(ActivatedRoute);

  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly error = signal<string | null>(null);
  readonly success = signal<string | null>(null);
  readonly ticket = signal<SupportTicket | null>(null);

  readonly statuses: SupportTicketStatus[] = [
    'pending',
    'in_progress',
    'resolved',
    'closed',
  ];

  editStatus: SupportTicketStatus | string = 'pending';
  editNotes = '';

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) {
      this.error.set('Missing support ticket id');
      return;
    }
    this.load(id);
  }

  load(id: string): void {
    this.loading.set(true);
    this.error.set(null);
    this.api.getSupportTicket(id).subscribe({
      next: (data) => {
        this.ticket.set(data);
        this.editStatus = data.status || 'pending';
        this.editNotes = data.admin_notes || '';
        this.loading.set(false);
      },
      error: (err: { message?: string }) => {
        this.error.set(err?.message || 'Failed to load support ticket');
        this.loading.set(false);
      },
    });
  }

  save(): void {
    const row = this.ticket();
    if (!row || this.saving()) return;

    this.saving.set(true);
    this.error.set(null);
    this.success.set(null);

    this.api
      .updateSupportTicket(row.id, {
        status: this.editStatus,
        admin_notes: this.editNotes.trim() || null,
      })
      .subscribe({
        next: (data) => {
          this.ticket.set(data);
          this.editStatus = data.status || 'pending';
          this.editNotes = data.admin_notes || '';
          this.saving.set(false);
          this.success.set('Support ticket updated');
        },
        error: (err: { message?: string }) => {
          this.error.set(err?.message || 'Failed to update support ticket');
          this.saving.set(false);
        },
      });
  }

  userLabel(row: SupportTicket): string {
    const u = row.user;
    if (!u) return `User #${row.user_id}`;
    const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
    return name || u.email || `User #${row.user_id}`;
  }

  statusLabel(status: string | null | undefined): string {
    if (!status) return '—';
    return status.replace(/_/g, ' ');
  }
}
